import { Calendar, Building2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useDivisions } from '@/hooks/useDivisions';
import type { DashboardFilters } from '@/hooks/useDashboardFilters';

interface DashboardFilterBarProps {
  filters: DashboardFilters;
  onUpdateYear: (year: number | 'all') => void;
  onToggleDivision: (divisionId: string) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

export function DashboardFilterBar({
  filters,
  onUpdateYear,
  onToggleDivision,
  onReset,
  hasActiveFilters,
}: DashboardFilterBarProps) {
  const { divisions, isLoading: divisionsLoading } = useDivisions();
  const currentYear = new Date().getFullYear();

  // Generate year options (current year and 4 previous years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const selectedDivisionsCount = filters.divisionIds.length;
  const selectedDivisions = divisions.filter(d => filters.divisionIds.includes(d.id));

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Year Selector */}
          <Select
            value={filters.year.toString()}
            onValueChange={(value) => onUpdateYear(value === 'all' ? 'all' : parseInt(value))}
          >
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Division Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Building2 className="h-4 w-4 mr-2" />
                Divisions
                {selectedDivisionsCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedDivisionsCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] z-50">
              <div className="space-y-4">
                <div className="font-medium text-sm">Filter by Division</div>
                {divisionsLoading ? (
                  <div className="text-sm text-muted-foreground">Loading divisions...</div>
                ) : divisions.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No divisions found</div>
                ) : (
                  <div className="space-y-2">
                    {divisions.map((division) => (
                      <div key={division.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`division-${division.id}`}
                          checked={filters.divisionIds.includes(division.id)}
                          onCheckedChange={() => onToggleDivision(division.id)}
                        />
                        <label
                          htmlFor={`division-${division.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {division.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Active Filter Badges */}
          {hasActiveFilters && (
            <>
              <div className="flex gap-2 items-center flex-wrap">
                {filters.year !== currentYear && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.year === 'all' ? 'All Time' : filters.year}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => onUpdateYear(currentYear)}
                    />
                  </Badge>
                )}
                {selectedDivisions.map((division) => (
                  <Badge key={division.id} variant="secondary" className="gap-1">
                    {division.name}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => onToggleDivision(division.id)}
                    />
                  </Badge>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={onReset}>
                Clear all
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
